import React, { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWizardStore } from '../components/wizard/useWizardStore';
import StepUpload from '../components/wizard/StepUpload';
import StepOutline from '../components/wizard/StepOutline';
// StepGenerate/StepExport 已废弃，替换为 BidWritingWord
import api from '../services/api';
import { getSearchConfig } from '../services/ragApi';
import { toast } from '../store/toastStore';
import { imagePlacementApi } from '../services/imagePlacementApi';

const BidWritingWizard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentStep = useWizardStore((s) => s.currentStep);
  const setCurrentStep = useWizardStore((s) => s.setCurrentStep);
  const project = useWizardStore((s) => s.project);
  const setProject = useWizardStore((s) => s.setProject);
  const setUseRAG = useWizardStore((s) => s.setUseRAG);
  const setUseHistoricalData = useWizardStore((s) => s.setUseHistoricalData);
  const setUseImageLibrary = useWizardStore((s) => s.setUseImageLibrary);
  const setSelectedImageCategoryIds = useWizardStore((s) => s.setSelectedImageCategoryIds);
  const setHistoricalChapterRefs = useWizardStore((s) => s.setHistoricalChapterRefs);

  const useRAG = useWizardStore((s) => s.useRAG);
  const useHistoricalData = useWizardStore((s) => s.useHistoricalData);
  const useImageLibrary = useWizardStore((s) => s.useImageLibrary);
  const selectedImageCategoryIds = useWizardStore((s) => s.selectedImageCategoryIds);
  const historicalChapterRefs = useWizardStore((s) => s.historicalChapterRefs);

  const ragSaveRef = useRef<ReturnType<typeof setTimeout>>();

  const saveRagConfig = useCallback(() => {
    if (!id) return;
    const config = {
      use_web_search: useRAG,
      use_historical_data: useHistoricalData,
      historical_chapter_refs: historicalChapterRefs,
      use_image_library: useImageLibrary,
      image_category_ids: selectedImageCategoryIds,
    };
    api.put(`/api/projects/${id}`, { rag_config: config })
      .then(() => {})
      .catch(() => {});
  }, [id, useRAG, useHistoricalData, historicalChapterRefs, useImageLibrary, selectedImageCategoryIds]);

  useEffect(() => {
    if (!project) return;
    if (ragSaveRef.current) clearTimeout(ragSaveRef.current);
    ragSaveRef.current = setTimeout(saveRagConfig, 500);
    return () => { if (ragSaveRef.current) clearTimeout(ragSaveRef.current); };
  }, [saveRagConfig, project]);

  const loadProject = async () => {
    try {
      const response = await api.get(`/api/projects/${id}`);
      setProject(response.data);

      const rc = response.data.rag_config || {};
      if (rc.use_web_search !== undefined) setUseRAG(rc.use_web_search);
      if (rc.use_historical_data !== undefined) setUseHistoricalData(rc.use_historical_data);
      if (rc.historical_chapter_refs) setHistoricalChapterRefs(rc.historical_chapter_refs);
      if (rc.use_image_library !== undefined) setUseImageLibrary(rc.use_image_library);
      if (rc.image_category_ids) setSelectedImageCategoryIds(rc.image_category_ids);

      getSearchConfig()
        .then(() => {})
        .catch(() => {});

      imagePlacementApi.list(parseInt(id!)).catch(() => {});
    } catch (error) {
      console.error('加载项目失败:', error);
      toast.error('加载项目失败');
      navigate('/projects');
    }
  };

  useEffect(() => {
    if (id) {
      loadProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">加载中...</div>
        </div>
      </div>
    );
  }

  switch (currentStep) {
    case 1: return <StepUpload />;
    case 2: return <StepOutline />;
    default: return <StepUpload />;
  }
};

export default BidWritingWizard;
